import React, { useState, useEffect } from "react";
import { OpenAPISpec, ParsedEndpoint } from "../types";
import { resolveRef } from "../utils/openapi";
import { METHOD_COLORS } from "./Sidebar";
import { cn } from "../utils/cn";
import { Play, Sparkles } from "lucide-react";
import { TryItOutModal } from "./TryItOutModal";
import { buildApiUrl } from "../config/api";
import { FormattedMessage } from "./FormattedMessage";

interface EndpointViewProps {
  spec: OpenAPISpec;
  endpoint: ParsedEndpoint;
  credentials: Record<string, string>;
  user?: { username: string; email?: string };
}

export function EndpointView({
  spec,
  endpoint,
  credentials,
  user,
}: EndpointViewProps) {
  const { method, path, operation } = endpoint;
  const [isTryItOutOpen, setIsTryItOutOpen] = useState(false);
  
  // AI Explanation state
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [showAIExplanation, setShowAIExplanation] = useState(false);

  useEffect(() => {
    // Reset AI explanation when endpoint changes
    setAiExplanation(null);
    setShowAIExplanation(false);
  }, [endpoint.id]);

  const fetchAIExplanation = async () => {
    if (aiExplanation) {
      setShowAIExplanation(true);
      return;
    }
    
    setIsLoadingAI(true);
    try {
      const res = await fetch(buildApiUrl("/api/ai/explain"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          endpoint: {
            method,
            path,
            summary: operation.summary,
            description: operation.description,
          },
          spec: {
            title: spec.info.title,
            description: spec.info.description,
          },
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setAiExplanation(data.explanation);
        setShowAIExplanation(true);
      }
    } catch (error) {
      console.error("Failed to fetch AI explanation:", error);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const renderSchema = (schema: any, level = 0): React.ReactNode => {
    if (!schema) return null;

    let resolved = schema;
    if (schema.$ref) {
      resolved = resolveRef(spec, schema.$ref) || schema;
    }

    if (resolved.type === "object" || resolved.properties) {
      return (
        <div className="space-y-2 text-[13px] font-mono leading-relaxed">
          <div className="text-zinc-400">{"{"}</div>
          <div className="pl-4 space-y-3 border-l border-zinc-200/60 ml-1.5">
            {Object.entries(resolved.properties || {}).map(
              ([key, prop]: [string, any]) => {
                const propResolved = prop.$ref
                  ? resolveRef(spec, prop.$ref) || prop
                  : prop;
                const isRequired = resolved.required?.includes(key);
                return (
                  <div key={key} className="flex flex-col gap-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-zinc-900 font-medium">{key}</span>
                        {isRequired && (
                          <span className="text-[9px] text-red-600 uppercase tracking-widest font-sans font-bold bg-red-50 px-1.5 py-0.5">
                            req
                          </span>
                        )}
                      <span className="text-zinc-500">
                        {propResolved.type || "any"}
                      </span>
                      {propResolved.format && (
                        <span className="text-zinc-400 text-xs">
                          ({propResolved.format})
                        </span>
                      )}
                    </div>
                    {propResolved.description && (
                      <div className="text-zinc-500 text-xs font-sans mt-0.5 max-w-2xl">
                        {propResolved.description}
                      </div>
                    )}
                    {propResolved.type === "object" ||
                    propResolved.properties ? (
                      <div className="mt-2">
                        {renderSchema(propResolved, level + 1)}
                      </div>
                    ) : propResolved.type === "array" ? (
                      <div className="mt-2 pl-4 border-l border-zinc-200/60 ml-1.5">
                        <span className="text-zinc-400">[</span>
                        <div className="pl-4 py-1">
                          {renderSchema(propResolved.items, level + 1)}
                        </div>
                        <span className="text-zinc-400">]</span>
                      </div>
                    ) : null}
                  </div>
                );
              },
            )}
          </div>
          <div className="text-zinc-400">{"}"}</div>
        </div>
      );
    }

    if (resolved.type === "array") {
      return (
        <div className="space-y-1 text-[13px] font-mono">
          <span className="text-zinc-400">[</span>
          <div className="pl-4 border-l border-zinc-200/60 ml-1.5 py-1">
            {renderSchema(resolved.items, level + 1)}
          </div>
          <span className="text-zinc-400">]</span>
        </div>
      );
    }

    return (
      <span className="text-[13px] font-mono text-zinc-600">
        {resolved.type || "any"}
        {resolved.format && (
          <span className="text-zinc-400 ml-1">({resolved.format})</span>
        )}
      </span>
    );
  };

  const renderParameters = () => {
    if (!operation.parameters || operation.parameters.length === 0) return null;

    return (
      <div className="mb-12">
        <h3 className="text-xs font-bold text-zinc-900 mb-4 uppercase tracking-widest">
          Parameters
        </h3>
        <div className="overflow-x-auto border border-zinc-200">
          <table className="w-full min-w-[600px] text-left text-sm">
            <thead className="bg-zinc-50 text-zinc-500 border-b border-zinc-200">
              <tr>
                <th className="px-5 py-3.5 font-medium text-xs tracking-wider">NAME</th>
                <th className="px-5 py-3.5 font-medium text-xs tracking-wider">IN</th>
                <th className="px-5 py-3.5 font-medium text-xs tracking-wider">TYPE</th>
                <th className="px-5 py-3.5 font-medium text-xs tracking-wider">DESCRIPTION</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 bg-white">
              {operation.parameters.map((param, i) => {
                const resolvedParam = param.$ref
                  ? resolveRef(spec, param.$ref) || param
                  : param;
                const schema = resolvedParam.schema || resolvedParam;
                return (
                  <tr key={i} className="hover:bg-zinc-50/50 transition-colors">
                    <td className="px-5 py-4 font-mono text-zinc-900 text-[13px]">
                      {resolvedParam.name}
                      {resolvedParam.required && (
                        <span className="ml-2 text-[9px] text-red-600 uppercase tracking-widest font-sans font-bold bg-red-50 px-1.5 py-0.5">
                          req
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-zinc-500 text-[13px] uppercase">
                      {resolvedParam.in}
                    </td>
                    <td className="px-5 py-4 font-mono text-zinc-500 text-[13px]">
                      {schema.type || "string"}
                    </td>
                    <td className="px-5 py-4 text-zinc-600 text-[13px] leading-relaxed">
                      {resolvedParam.description || "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderRequestBody = () => {
    if (!operation.requestBody) return null;

    const resolvedBody = operation.requestBody.$ref
      ? resolveRef(spec, operation.requestBody.$ref) || operation.requestBody
      : operation.requestBody;
    const content = resolvedBody.content;
    if (!content) return null;

    const jsonContent = content["application/json"];
    if (!jsonContent) return null;

    return (
      <div className="mb-12">
        <h3 className="text-xs font-bold text-zinc-900 mb-4 uppercase tracking-widest flex items-center gap-2">
          Request Body
          {resolvedBody.required && (
            <span className="text-[9px] text-red-600 uppercase tracking-widest font-bold bg-red-50 px-1.5 py-0.5">
              req
            </span>
          )}
        </h3>
        <div className="bg-white border border-zinc-200 p-6">
          <div className="text-[10px] font-mono text-zinc-400 mb-5 uppercase tracking-widest">
            application/json
          </div>
          {renderSchema(jsonContent.schema)}
        </div>
      </div>
    );
  };

  const renderResponses = () => {
    if (!operation.responses) return null;

    return (
      <div className="mb-12">
        <h3 className="text-xs font-bold text-zinc-900 mb-4 uppercase tracking-widest">
          Responses
        </h3>
        <div className="space-y-4">
          {Object.entries(operation.responses).map(
            ([code, response]: [string, any]) => {
              const resolvedResponse = response.$ref
                ? resolveRef(spec, response.$ref) || response
                : response;
              const isSuccess = code.startsWith("2");

              return (
                <div
                  key={code}
                  className="border border-zinc-200 overflow-hidden bg-white"
                >
                  <div
                    className={cn(
                      "px-5 py-4 border-b flex items-center gap-4",
                      isSuccess
                        ? "bg-emerald-50 border-emerald-100"
                        : "bg-zinc-50 border-zinc-100",
                    )}
                  >
                    <span
                      className={cn(
                        "font-mono font-bold text-[13px] px-2 py-1",
                        isSuccess
                          ? "text-emerald-700 bg-emerald-100"
                          : "text-zinc-700 bg-zinc-200",
                      )}
                    >
                      {code}
                    </span>
                    <span className="text-[13px] text-zinc-600 font-medium">
                      {resolvedResponse.description}
                    </span>
                  </div>

                  {resolvedResponse.content &&
                    resolvedResponse.content["application/json"] && (
                      <div className="p-6 bg-white">
                        <div className="text-[10px] font-mono text-zinc-400 mb-5 uppercase tracking-widest">
                          application/json
                        </div>
                        {renderSchema(
                          resolvedResponse.content["application/json"].schema,
                        )}
                      </div>
                    )}
                </div>
              );
            },
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="mb-8 sm:mb-12 pb-8 sm:pb-12 border-b border-zinc-200">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-3 min-w-0">
            <span
              className={cn(
                "text-[10px] font-bold px-2 py-1 border uppercase tracking-widest shrink-0 mt-1",
                METHOD_COLORS[method] ||
                  "text-zinc-600 bg-zinc-50 border-zinc-200",
              )}
            >
              {method}
            </span>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-mono text-zinc-900 break-all tracking-tight">
                {path}
              </h1>
              {operation.summary && (
                <p className="text-sm text-zinc-500 mt-2">
                  {operation.summary}
                </p>
              )}
            </div>
          </div>

          <button
            onClick={() => setIsTryItOutOpen(true)}
            className="flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-xs font-medium tracking-wider hover:bg-zinc-800 transition-colors shrink-0"
          >
            <Play className="w-3.5 h-3.5" />
            TRY IT OUT
          </button>
        </div>

        {operation.description && (
          <p className="text-zinc-600 leading-relaxed text-sm max-w-3xl">
            {operation.description}
          </p>
        )}

        {/* AI Explanation - Only for logged-in users */}
        {user && (
          <div className="mt-6">
            {!showAIExplanation ? (
              <button
                onClick={fetchAIExplanation}
                disabled={isLoadingAI}
                className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-200 text-xs font-medium tracking-wider text-zinc-700 hover:border-zinc-900 hover:text-zinc-900 transition-all"
              >
                {isLoadingAI ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
                    LOADING...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    {aiExplanation ? "SHOW AI EXPLANATION" : "GET AI EXPLANATION"}
                  </>
                )}
              </button>
            ) : (
              <div className="border border-zinc-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-zinc-900">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-xs font-bold tracking-wider">AI EXPLANATION</span>
                  </div>
                  <button
                    onClick={() => setShowAIExplanation(false)}
                    className="text-xs text-zinc-500 hover:text-zinc-900 tracking-wider"
                  >
                    HIDE
                  </button>
                </div>
                <div className="text-zinc-700 text-sm leading-relaxed">
                  {aiExplanation ? (
                    <FormattedMessage content={aiExplanation} />
                  ) : (
                    <p>{operation.summary || "No explanation available"}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {renderParameters()}
      {renderRequestBody()}
      {renderResponses()}

      {isTryItOutOpen && (
        <TryItOutModal
          spec={spec}
          endpoint={endpoint}
          credentials={credentials}
          onClose={() => setIsTryItOutOpen(false)}
        />
      )}
    </div>
  );
}
